'use client'

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { IChildrenProps } from '@/utils/typings/children-props'
import { useAuth, useClerk } from '@clerk/nextjs'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { UrlConfig } from '@/setup/routing/routing'
import { pushGTMEvent } from '@/features/third-party/google-tag-manager/google-tag-manager'
import { AUTH_STEPS, IAuthStep } from '@/features/auth/helpers/auth-constants'
import { ATTRIBUTION_QUERY_PARAMS } from '@/features/attribution/utils/constants'

interface ILandingAuthContext {
    isAuthModalOpen: boolean
    initialAuthStep: IAuthStep
    boardReferralCode: string | null | undefined
    registerBoardReferralCode: (referralCode: string | null) => () => void
    openAuthModal: (initialStep?: IAuthStep, redirectUrl?: string) => void
    closeAuthModal: () => void
    /** Analytics-tracked CTA handler: opens auth modal for logged-out users, redirects signed-in users to the app. */
    handleCtaClick: () => void
}

const LandingAuthContext = createContext<ILandingAuthContext | null>(null)

interface IBoardReferralRegistration {
    id: symbol
    referralCode: string | null
}

/** Reads the pre-paint, non-authoritative Clerk session hint used by landing CTAs. */
export function hasLandingSignedInHint() {
    return typeof document !== 'undefined' && document.documentElement.dataset.landingAuthHint === 'signed-in'
}

export function LandingAuthProvider({ children }: IChildrenProps) {
    const { isSignedIn } = useAuth()
    const clerk = useClerk()
    const router = useRouter()
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
    const [initialAuthStep, setInitialAuthStep] = useState<IAuthStep>(AUTH_STEPS.SignUp)
    const [hasModalRedirectUrl, setHasModalRedirectUrl] = useState(false)
    const [boardReferralCode, setBoardReferralCode] = useState<string | null | undefined>(undefined)
    const boardReferralRegistrationRef = useRef<IBoardReferralRegistration | null>(null)

    useEffect(() => {
        if (clerk.loaded) {
            document.documentElement.dataset.landingAuthHint = clerk.session ? 'signed-in' : 'signed-out'
        }

        return clerk.addListener(
            ({ session }) => {
                document.documentElement.dataset.landingAuthHint = session ? 'signed-in' : 'signed-out'
            },
            { skipInitialEmit: true }
        )
    }, [clerk])

    const registerBoardReferralCode = useCallback((referralCode: string | null) => {
        const registration = { id: Symbol('board-referral-registration'), referralCode }
        boardReferralRegistrationRef.current = registration

        return () => {
            if (boardReferralRegistrationRef.current?.id === registration.id) {
                boardReferralRegistrationRef.current = null
            }
        }
    }, [])

    const openAuthModal = useCallback(
        (step: IAuthStep = AUTH_STEPS.SignUp, redirectUrl?: string) => {
            if (redirectUrl) {
                const nextSearchParams = new URLSearchParams(searchParams)
                nextSearchParams.set(ATTRIBUTION_QUERY_PARAMS.RedirectUrl, redirectUrl)
                router.replace(`${pathname}?${nextSearchParams.toString()}`, { scroll: false })
                setHasModalRedirectUrl(true)
            }

            setBoardReferralCode(boardReferralRegistrationRef.current?.referralCode)
            setInitialAuthStep(step)
            setIsAuthModalOpen(true)
        },
        [pathname, router, searchParams]
    )

    const closeAuthModal = useCallback(() => {
        setIsAuthModalOpen(false)
        setBoardReferralCode(undefined)

        if (!hasModalRedirectUrl) return

        const nextSearchParams = new URLSearchParams(searchParams)
        nextSearchParams.delete(ATTRIBUTION_QUERY_PARAMS.RedirectUrl)
        const queryString = nextSearchParams.toString()
        router.replace(`${pathname}${queryString ? `?${queryString}` : ''}`, { scroll: false })
        setHasModalRedirectUrl(false)
    }, [hasModalRedirectUrl, pathname, router, searchParams])

    const handleCtaClick = useCallback(() => {
        pushGTMEvent({ event: 'cta_button_clicked' })
        // PostHog cta_clicked is fired by the delegated [data-cta] listener
        // (tracking/landing-cta-tracking.tsx) — firing here too would double-count.

        if (!isSignedIn && !hasLandingSignedInHint()) {
            openAuthModal()
            return
        }

        router.push(UrlConfig['/routing'].getUrl())
    }, [isSignedIn, router, openAuthModal])

    return (
        <LandingAuthContext.Provider
            value={{
                isAuthModalOpen,
                initialAuthStep,
                boardReferralCode,
                registerBoardReferralCode,
                openAuthModal,
                closeAuthModal,
                handleCtaClick,
            }}
        >
            {children}
        </LandingAuthContext.Provider>
    )
}

export function useLandingAuthContext() {
    const context = useContext(LandingAuthContext)
    if (!context) throw new Error('LandingAuthContext not found')
    return context
}
